import React, { useMemo } from 'react'

import { mainnet } from 'viem/chains'

import { HighlightedText } from '~/components/HighlightedText'
import { SplitsLink } from '~/components/Link'
import { CLLayout } from '~/components/component-library'
import { AddressDisplayAvatar } from '~/components/component-library/AddressDisplay/AddressDisplayAvatar'
import { AddressDisplayBadges } from '~/components/component-library/AddressDisplay/AddressDisplayBadges'
import { AddressPopover } from '~/components/component-library/AddressDisplay/AddressPopover'
import {
  AddressDisplayProps,
  AddressDisplaySize,
} from '~/components/component-library/AddressDisplay/types'
import { SupportedChainId } from '~/constants/chains'
import { useDisplayName } from '~/hooks/useDisplayName'
import { usePrivacyMode } from '~/hooks/usePrivacyMode'
import { HIDDEN_ADDRESS } from '~/store/privacyModeStore'
import { IAddress } from '~/types'
import {
  getExplorerAddressLinkChainId,
  shortenAddress,
} from '~/utils/addresses'
import { cn } from '~/utils/tailwind-utils'

export type {
  AddressDisplayProps,
  AddressDisplaySize,
} from '~/components/component-library/AddressDisplay/types'

// Avatar pixel sizes per layout.
const STANDARD_AVATAR_SIZE: Record<AddressDisplaySize, number> = {
  small: 16,
  base: 24,
  large: 32,
}
const TWO_LINE_AVATAR_SIZE: Record<AddressDisplaySize, number> = {
  small: 32,
  base: 36,
  large: 44,
}

/**
 * AddressDisplay is a component that displays an Ethereum account.
 *
 * @param address
 * @param chainId
 * @param layout
 * @param size - Text size variant (standard and two-line layouts): "small" for compact displays, "base" for default, "large" for hero contexts like an account-detail header
 * @param hidePopover - Use inside dropdowns and hover cards, when you want to prevent the popover from opening
 * @param isInternal - Use with lists of internal accounts (removes unnecessary addresses and icons)
 * @param popoverEditDisabled - Use to disable the edit contact button in the popover
 * @param highlightText - Marks occurrences of this string within the display name
 * @param clickable - Set to false when a surrounding control owns the click
 */
const AddressDisplayBase = ({
  address,
  chainId,
  layout = 'standard',
  size = 'base',
  hidePopover = false,
  isInternal = false,
  popoverEditDisabled = false,
  highlightText,
  clickable = true,
}: AddressDisplayProps) => {
  const {
    displayName,
    ens,
    smartAccountName,
    orgLabel,
    farcaster,
    payee,
    bankAccount,
    automation,
  } = useDisplayName(address, chainId)
  const isPrivacyModeOn = usePrivacyMode()

  const isBank = !!bankAccount
  const isAutomation = !!automation
  const isInContacts = smartAccountName || orgLabel
  const hasBadges = isInContacts || ens || farcaster || payee || isAutomation
  const noDisplayName =
    !isInContacts && !farcaster && !ens && !payee && !isBank && !isAutomation
  const showBadges = !isInternal && !!hasBadges

  // Memoize the display content so AddressPopover receives a stable children
  // reference and React.memo can actually prevent unnecessary re-renders.
  const content = useMemo(
    () =>
      buildAddressDisplay({
        address,
        chainId,
        layout,
        size,
        hidePopover,
        isInternal,
        noDisplayName,
        displayName,
        showBadges,
        highlightText,
        isPrivacyModeOn,
      }),
    [
      address,
      chainId,
      layout,
      size,
      hidePopover,
      isInternal,
      noDisplayName,
      displayName,
      showBadges,
      highlightText,
      isPrivacyModeOn,
    ],
  )

  const isSplitsAccount = !!smartAccountName || !!automation
  const path = isSplitsAccount
    ? `/accounts/${address}`
    : getExplorerAddressLinkChainId(address, chainId ?? mainnet.id)
  const linkedContent = clickable ? (
    <SplitsLink
      path={path}
      target={isSplitsAccount ? undefined : 'tab'}
      onClick={(event) => event.stopPropagation()}
    >
      {content}
    </SplitsLink>
  ) : (
    content
  )

  if (hidePopover) return linkedContent

  return (
    <AddressPopover
      address={address}
      chainId={chainId}
      fit={layout === 'inline-text'}
      editDisabled={popoverEditDisabled}
    >
      {linkedContent}
    </AddressPopover>
  )
}

function buildAddressDisplay({
  address,
  chainId,
  layout,
  size,
  hidePopover,
  isInternal,
  noDisplayName,
  displayName,
  showBadges,
  highlightText,
  isPrivacyModeOn,
}: {
  address: IAddress
  chainId?: SupportedChainId
  layout: CLLayout
  size: AddressDisplaySize
  hidePopover?: boolean
  isInternal?: boolean
  noDisplayName?: boolean
  displayName?: string
  showBadges?: boolean
  highlightText?: string
  isPrivacyModeOn: boolean
}) {
  const AddressDisplaySecondaryName = () => {
    if (noDisplayName) return null

    if (isInternal && layout !== 'two-line') return null

    return (
      <div
        className={cn('flex flex-shrink-0 items-center text-secondary', {
          'h-3 text-xs leading-4': size === 'small',
          'h-4 text-sm leading-4': size === 'base',
          'h-5 text-base leading-5': size === 'large',
        })}
      >
        {isPrivacyModeOn ? HIDDEN_ADDRESS : shortenAddress(address)}
      </div>
    )
  }

  switch (layout) {
    case 'standard':
      return (
        <AddressDisplayInner
          className={cn({
            'text-sm': size === 'small',
            'text-base': size === 'base',
            'text-lg': size === 'large',
          })}
          size={size}
        >
          <AddressDisplayAvatar
            address={address}
            chainId={chainId}
            size={STANDARD_AVATAR_SIZE[size]}
          />
          <AddressDisplayName
            name={displayName}
            size={size}
            highlightText={highlightText}
          />
          <AddressDisplayBadges
            address={address}
            chainId={chainId}
            showBadges={showBadges}
            size={size}
          />
          <AddressDisplaySecondaryName />
        </AddressDisplayInner>
      )
    case 'two-line':
      return (
        <div
          className={cn('flex w-full items-center gap-2', {
            'text-sm': size === 'small',
            'text-base': size === 'base',
            'text-lg': size === 'large',
          })}
        >
          <div className="mt-0.5 flex-shrink-0">
            <AddressDisplayAvatar
              address={address}
              chainId={chainId}
              size={TWO_LINE_AVATAR_SIZE[size]}
            />
          </div>
          <div className="flex w-full min-w-0 flex-col items-start justify-center gap-0.5 leading-tight">
            <AddressDisplayInner size={size}>
              <AddressDisplayName
                name={displayName}
                size={size}
                highlightText={highlightText}
              />
              <AddressDisplayBadges
                address={address}
                chainId={chainId}
                showBadges={showBadges}
                size={size}
              />
            </AddressDisplayInner>
            <AddressDisplaySecondaryName />
          </div>
        </div>
      )
    case 'inline-text':
      return (
        <span
          className={cn([
            'font-medium',
            !hidePopover &&
              'underline decoration-gray-300 decoration-dashed underline-offset-4 dark:decoration-gray-600',
          ])}
        >
          {displayName}
        </span>
      )
  }
}

const AddressDisplayInner = ({
  children,
  className,
  size = 'base',
}: {
  children: React.ReactNode
  className?: string
  size?: AddressDisplaySize
}) => {
  return (
    <span
      className={cn(
        'flex min-w-0 max-w-full items-center truncate',
        {
          'h-5 space-x-1.5 leading-5': size === 'small',
          'h-6 space-x-2 leading-6': size === 'base',
          'h-8 space-x-2.5 leading-8': size === 'large',
        },
        className,
      )}
    >
      {children}
    </span>
  )
}

const AddressDisplayName = ({
  name,
  size = 'base',
  highlightText,
}: {
  name?: string
  size?: AddressDisplaySize
  highlightText?: string
}) => {
  if (!name) return null
  return (
    <span
      className={cn('flex-0 min-w-0 truncate text-primary', {
        'font-medium': size === 'large',
      })}
    >
      <HighlightedText text={name} highlight={highlightText} />
    </span>
  )
}

export const AddressDisplay = React.memo(AddressDisplayBase)
AddressDisplay.displayName = 'AddressDisplay'
